import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

const sections = [
  {
    title: '1. Introduction',
    content: [
      'Welcome to this website, mobile application, and related services (the "Service"), operated by Syntera Solutions LTD ("Company", "we", "our", or "us").',
      'Registered Office:\nInternational House\n64 Nile Street\nLondon\nN1 7SR\nUnited Kingdom',
      'Email: help@syntera-solutions.com',
      'Phone: +44 7520 640897',
      'These Terms of Use ("Terms") govern your access to and use of the Service, including any content, features, and functionality.',
      'By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use the Service.',
    ],
  },
  {
    title: '2. Definitions',
    content: [
      '"Service" means the website, mobile applications, and all related functionality',
      '"Company" refers to Syntera Solutions LTD',
      '"User" / "you" means any individual using the Service',
      '"Content" means all materials available through the Service',
    ],
  },
  {
    title: '3. Eligibility',
    content: [
      'By using the Service, you confirm that:',
      'You are at least 18 years old or meet the legal age requirement in your jurisdiction',
      'You have the legal capacity to enter into these Terms',
      'You will comply with all applicable laws',
    ],
  },
  {
    title: '4. Use of the Service',
    content: [
      'You agree to use the Service only for lawful purposes.',
      'You must not:',
      'Violate any applicable laws or regulations',
      'Attempt unauthorized access to systems or data',
      'Interfere with the operation or security of the Service',
      'Use automated tools (bots, scrapers) without permission',
      'Use the Service in a way that could harm the Company or other users',
    ],
  },
  {
    title: '5. Subscriptions and Payments',
    content: [
      'Some features of the Service may require a subscription or payment.',
      'Payments made via the Apple App Store are processed by Apple',
      'Subscriptions may automatically renew unless canceled',
      'You can manage or cancel subscriptions in your Apple ID settings',
      "All billing and refunds for App Store purchases are handled in accordance with Apple's policies.",
    ],
  },
  {
    title: '6. Intellectual Property',
    content: [
      'All Content is owned by or licensed to the Company and protected by applicable intellectual property laws.',
      'You are granted a limited, non-exclusive, non-transferable license to use the Service for personal purposes.',
      'You may not:',
      'Copy or distribute Content without permission',
      'Modify or create derivative works',
      'Use Company trademarks or branding without consent',
    ],
  },
  {
    title: '7. User Content',
    content: [
      'If you submit content or communications:',
      'You retain ownership',
      'You grant us a limited right to use it to operate the Service',
      'You confirm it does not violate laws or third-party rights',
    ],
  },
  {
    title: '8. Third-Party Services',
    content: [
      'The Service may include links or integrations with third-party services.',
      'We are not responsible for:',
      'Third-party content or services',
      'Their availability or policies',
      'Use of third-party services is at your own risk.',
    ],
  },
  {
    title: '9. Disclaimer',
    content: [
      'The Service is provided "as is" and "as available".',
      'We do not guarantee:',
      'Accuracy or completeness of content',
      'Continuous or error-free operation',
      'That the Service will meet all expectations',
    ],
  },
  {
    title: '10. Limitation of Liability',
    content: [
      'To the fullest extent permitted by law, the Company is not liable for:',
      'Indirect or consequential damages',
      'Loss of data, revenue, or business opportunities',
      'Issues arising from use or inability to use the Service',
      'Nothing limits liability where prohibited by law.',
    ],
  },
  {
    title: '11. User Responsibility',
    content: [
      'You are responsible for:',
      'Your use of the Service',
      'Maintaining device security',
      'Any activity conducted under your use',
    ],
  },
  {
    title: '12. Privacy',
    content: [
      'Your use of the Service is governed by our Privacy Policy.',
      'We process personal data in accordance with applicable laws, including UK GDPR, where applicable.',
    ],
  },
  {
    title: '13. Changes to the Service and Terms',
    content: [
      'We may:',
      'Modify or discontinue parts of the Service',
      'Update these Terms',
      'Continued use of the Service means acceptance of the updated Terms.',
    ],
  },
  {
    title: '14. Suspension and Termination',
    content: [
      'We may suspend or restrict access if:',
      'You violate these Terms',
      'Required by law',
      'Necessary to protect the Service or users',
    ],
  },
  {
    title: '15. Governing Law',
    content: ['These Terms are governed by the laws of England and Wales.'],
  },
  {
    title: '16. Severability',
    content: ['If any provision is invalid, the remaining provisions remain in effect.'],
  },
  {
    title: '17. Entire Agreement',
    content: ['These Terms constitute the entire agreement between you and the Company.'],
  },
  {
    title: '18. Contact',
    content: [
      'Syntera Solutions LTD',
      'International House',
      '64 Nile Street',
      'London',
      'N1 7SR',
      'United Kingdom',
      'help@syntera-solutions.com',
      '+44 7520 640897',
    ],
  },
];

export default function TermsPage() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[60vh] flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '40vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          TERMS
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-32 pb-16 max-w-3xl">
          <div
            className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-8 font-medium">
              &#8212;&nbsp;&nbsp;Legal information&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(56px, 8vw, 120px)', lineHeight: 0.9 }}
            >
              TERMS OF<br />
              <span style={{ color: '#FA6E4F' }}>USE.</span>
            </h1>
          </div>
          <div
            className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              Please read these terms carefully before using our services. By accessing the
              platform, you agree to be bound by the conditions below.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-10 lg:px-20 py-24 max-w-4xl mx-auto">
        <div className="flex flex-col gap-16">
          {sections.map((section, i) => (
            <div
              key={section.title}
              className={`transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${600 + i * 80}ms` }}
            >
              <h2 className="boldonse text-white text-2xl mb-6">{section.title}</h2>
              <div className="flex flex-col gap-3">
                {section.content.map((line, j) => (
                  <p
                    key={j}
                    className="text-white/70 text-base leading-relaxed whitespace-pre-line"
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}